// Lean static-data loader for Atlas: every file comes from the R2 CDN, cache
// busted by the remote data-version manifest the site pipeline writes. No
// i18n, no Capacitor, no local /data probing — the same URLs the site uses,
// small enough to ship in a phone app shell.
export const CDN_BASE = "https://static.americanhealthprices.com/data";
const VERSION_URL = "https://static.americanhealthprices.com/config/data-version.json";

let version = "0";
let versionPromise: Promise<void> | null = null;
function ensureVersion(): Promise<void> {
  if (!versionPromise) {
    versionPromise = fetch(VERSION_URL, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { v?: string | number } | null) => { if (j?.v) version = String(j.v); })
      .catch(() => { /* keep "0"; the SW cache still works */ });
  }
  return versionPromise;
}

const inflight = new Map<string, Promise<unknown>>();

/** Fetch `<CDN>/data/<file>?v=<version>` as JSON. Returns null on any failure. */
export async function loadStaticData<T>(file: string, _opts?: { cdnOnly?: boolean }): Promise<T | null> {
  await ensureVersion();
  const url = `${CDN_BASE}/${file}?v=${version}`;
  const existing = inflight.get(url);
  if (existing) return existing as Promise<T | null>;
  const p = (async () => {
    try {
      const ctrl = new AbortController();
      const t = window.setTimeout(() => ctrl.abort(), 25_000);
      const r = await fetch(url, { signal: ctrl.signal });
      window.clearTimeout(t);
      if (!r.ok) return null;
      const ct = r.headers.get("content-type") || "";
      if (!/json|octet/.test(ct)) return null;
      return (await r.json()) as T;
    } catch {
      return null;
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p as Promise<T | null>;
}

// ---- scenario coverage shards -------------------------------------------------
export interface CoverageRow {
  h: string; hp: number; np?: number | null; mp?: number | null; cp?: number | null;
  mc?: number | null; tc?: number | null; ss?: number | null; pm?: string;
}
interface Shard { coverage?: CoverageRow[]; rows?: CoverageRow[] }

const LRU_MAX = 40;
const coverageCache = new Map<string, CoverageRow[]>();
let hidden: Set<string> | null = null;
let hiddenPromise: Promise<void> | null = null;
function ensureHidden(): Promise<void> {
  if (!hiddenPromise) {
    hiddenPromise = loadStaticData<{ hidden: string[] }>("hospital_suppressions.json")
      .then((s) => { hidden = new Set(s?.hidden ?? []); });
  }
  return hiddenPromise;
}

/** One scenario's cornerstone-qualified hospital rows, duplicates dropped. */
export async function loadScenarioCoverageFor(scenarioId: string): Promise<CoverageRow[]> {
  if (!scenarioId) return [];
  const cached = coverageCache.get(scenarioId);
  if (cached) { coverageCache.delete(scenarioId); coverageCache.set(scenarioId, cached); return cached; }
  const [shard] = await Promise.all([loadStaticData<Shard>(`scenario_coverage/${scenarioId}.json`), ensureHidden()]);
  const rows = (shard?.coverage ?? shard?.rows ?? []).filter((r) => !hidden?.has(r.h));
  coverageCache.set(scenarioId, rows);
  if (coverageCache.size > LRU_MAX) coverageCache.delete(coverageCache.keys().next().value as string);
  return rows;
}
